import { useQuery } from '@tanstack/react-query';
import { Card, Icon } from '@barev/ui';
import { useI18n, useTRPC } from '@barev/web';
import { useEffect, useState } from 'react';
import { LEVEL_NAME, type Level } from '../lib.tsx';
import { Qr } from '../qr.tsx';

/**
 * Клубная карта с живым QR (меняется каждую минуту — скриншот не сработает) и партнёры клуба:
 * что положено на твоём уровне и сколько осталось по лимиту.
 */
export function Perks() {
  const { t, locale } = useI18n();
  const trpc = useTRPC();
  const me = useQuery(trpc.client.me.queryOptions());
  const qr = useQuery({ ...trpc.client.cardQr.queryOptions(), refetchInterval: 20_000, refetchIntervalInBackground: false });
  const partners = useQuery(trpc.client.partners.queryOptions());
  const level = (me.data?.membership.level ?? 'BAREV') as Level;
  const left = useSecondsLeft(qr.data?.expiresAt);
  const name = (l: Record<string, string>) => l[locale] || l.ru || '';

  return (
    <main className="app app--hero">
      <section className="hero hero--card">
        <h1 className="hero__hello">{t('client.card.title')}</h1>
        <div className="qr-card" data-plan={level === 'BAREV' ? 'free' : level === 'BAREV_PLUS' ? 'plus' : 'elite'}>
          <div className="qr-card__top">
            <span>Barev Club</span>
            <b>{LEVEL_NAME[level]}</b>
          </div>
          <div className="qr-card__code">{qr.data ? <Qr value={qr.data.url} size={200} label={t('client.partners.qrLabel')} /> : <div style={{ width: 200, height: 200 }} />}</div>
          <div className="qr-card__meta">
            <span>
              <b>{me.data?.firstName}</b> · <span className="bc-num">{me.data?.cardCode}</span>
            </span>
            <span className="qr-card__timer">{t('client.partners.qrRefresh', { seconds: left })}</span>
          </div>
        </div>
        <p className="hero__note">{t('client.partners.howTo')}</p>
        <a href="#/membership" className="pill pill--pink">
          {t('client.card.plans')}
          <Icon name="arrow" size={18} />
        </a>
      </section>
      <h2 className="sect__title">{t('client.card.partners')}</h2>
      {partners.data?.length === 0 ? (
        <Card tone="perks" className="bc-stack">
          <h2 className="bc-h3">{t('client.perks.soonTitle')}</h2>
          <p style={{ margin: 0 }}>{t('client.perks.soonText')}</p>
        </Card>
      ) : (
        <ul className="partners">
          {partners.data?.map((p) => (
            <li key={p.id} className="partner">
              <span className="partner__cat">{t(`client.partners.cat.${p.category}`)}</span>
              <b className="partner__name">{p.name}</b>
              {p.address && <span className="bc-small bc-muted">{p.address}</span>}
              <ul className="offers">
                {(p.offers.length ? p.offers : [{ id: 'main', title: p.offer, eligible: true, remaining: null, minLevel: null, limit: 'NONE' as const, limitCount: 1 }]).map((o) => (
                  <li key={o.id} className="offer" data-eligible={o.eligible} data-used={o.remaining === 0}>
                    <span>{name(o.title)}</span>
                    <small>
                      {!o.eligible
                        ? t('client.partners.needLevel', { level: LEVEL_NAME[(o.minLevel ?? 'BAREV') as Level] })
                        : o.remaining === null
                          ? o.minLevel
                            ? LEVEL_NAME[o.minLevel as Level]
                            : t('client.partners.allMembers')
                          : o.remaining > 0
                            ? t(`client.partners.limit_${o.limit}`, { count: o.limitCount })
                            : t(`client.partners.used_${o.limit}`)}
                    </small>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function useSecondsLeft(expiresAt?: Date) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return expiresAt ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 1000)) : 60;
}
